/** Accounts: registering, confirming the e-mail, signing in and out, one's own profile. */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { baseApi, refreshSession, type RefreshOutcome } from '../baseApi';
import { sessionEnded, sessionStarted, userUpdated, type WithSession } from '../sessionSlice';
import type {
  ConfirmedOut,
  ConfirmIn,
  GuestIn,
  HandleOut,
  LoginIn,
  PasswordChangeIn,
  PasswordResetConfirmIn,
  RecoveryCodesOut,
  SecondFactorSetupOut,
  RegisteredOut,
  RegisterIn,
  SessionOut,
  UserOut,
  UserPatch,
  VerificationOut,
  VerifiedOut,
  VerifyEmailIn,
} from '../types';

export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Registering asks for no password and does not sign in: a link is mailed, and the
    // password is chosen on the page it leads to.
    register: build.mutation<RegisteredOut, RegisterIn>({
      query: (body) => ({ url: '/auth/register', method: 'POST', body }),
    }),
    // What the page that finishes a registration asks while it is filled in: whether
    // a typed handle can be had (or a free one for a name).
    checkHandle: build.query<HandleOut, { name?: string; handle?: string }>({
      query: (params) => ({ url: '/auth/handle', params }),
      keepUnusedDataFor: 0,
    }),
    // What a mailed link is for. Asking does not use the link up, so it is a query;
    // the token goes in the body to stay out of the server's access log.
    verification: build.query<VerificationOut, string>({
      query: (token) => ({ url: '/auth/verification', method: 'POST', body: { token } }),
      keepUnusedDataFor: 0,
    }),
    // The way into the tools at dev.: the server sets the cookie the gateway asks for.
    openDevSession: build.mutation<void, void>({
      query: () => ({ url: '/auth/dev-session', method: 'POST' }),
    }),
    // Confirms the address. With a password (an account that has none yet) it also signs in.
    verifyEmail: build.mutation<VerifiedOut, VerifyEmailIn>({
      query: (body) => ({ url: '/auth/verify-email', method: 'POST', body }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { session } = (await queryFulfilled).data;
          if (session) {
            dispatch(sessionStarted(session));
            // A guest's work in this browser became the new account's.
            dispatch(baseApi.util.invalidateTags(['Progress', 'Attempt', 'History']));
          }
        } catch {
          // Shown by the page.
        }
      },
    }),
    resendVerification: build.mutation<void, string>({
      query: (email) => ({ url: '/auth/resend-verification', method: 'POST', body: { email } }),
    }),
    login: build.mutation<SessionOut, LoginIn>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(sessionStarted((await queryFulfilled).data));
          // What was done as a guest is now the account's: progress and history are read again.
          dispatch(baseApi.util.invalidateTags(['Progress', 'Attempt', 'History']));
        } catch {
          // Shown by the form.
        }
      },
    }),
    // A guest's session, for doing something in public lessons without an account
    // (see `ensureSession`). The server gives back the session the browser has, if any.
    startGuest: build.mutation<SessionOut, GuestIn>({
      query: (body) => ({ url: '/auth/guest', method: 'POST', body }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(sessionStarted((await queryFulfilled).data));
        } catch {
          // Whoever asked shows it.
        }
      },
    }),
    logout: build.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } finally {
          // Sign out locally even if the server could not be reached.
          dispatch(sessionEnded());
          dispatch(baseApi.util.resetApiState());
        }
      },
    }),

    // The password typed again before changing other accounts: the token stands for it
    // for a few minutes (features/access-control/confirmation.tsx).
    confirmIdentity: build.mutation<ConfirmedOut, ConfirmIn>({
      query: (body) => ({ url: '/auth/confirm', method: 'POST', body }),
    }),
    // Every session ends with the change; this browser gets the new one in the answer.
    changePassword: build.mutation<SessionOut, PasswordChangeIn>({
      query: (body) => ({ url: '/auth/password', method: 'POST', body }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(sessionStarted((await queryFulfilled).data));
        } catch {
          // Shown by the form.
        }
      },
    }),
    requestPasswordReset: build.mutation<void, string>({
      query: (email) => ({ url: '/auth/password-reset', method: 'POST', body: { email } }),
    }),
    // Whose password a mailed link is for; does not use the link up.
    passwordResetLink: build.query<VerificationOut, string>({
      query: (token) => ({ url: '/auth/password-reset/verification', method: 'POST', body: { token } }),
      keepUnusedDataFor: 0,
    }),
    resetPassword: build.mutation<void, PasswordResetConfirmIn>({
      query: (body) => ({ url: '/auth/password-reset/confirm', method: 'POST', body }),
    }),
    beginSecondFactor: build.mutation<SecondFactorSetupOut, void>({
      query: () => ({ url: '/auth/second-factor/setup', method: 'POST' }),
    }),
    enableSecondFactor: build.mutation<RecoveryCodesOut, string>({
      query: (code) => ({ url: '/auth/second-factor/enable', method: 'POST', body: { code } }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // The account has it now: what it may do changes with that.
          const me = await dispatch(authApi.endpoints.me.initiate(undefined, { forceRefetch: true })).unwrap();
          dispatch(userUpdated(me));
        } catch {
          // Shown by the form.
        }
      },
    }),
    me: build.query<UserOut, void>({
      query: () => ({ url: '/users/me' }),
      keepUnusedDataFor: 0,
    }),
    logoutEverywhere: build.mutation<void, void>({
      query: () => ({ url: '/auth/logout-everywhere', method: 'POST' }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } finally {
          dispatch(sessionEnded());
          dispatch(baseApi.util.resetApiState());
        }
      },
    }),

    updateMe: build.mutation<UserOut, UserPatch>({
      query: (body) => ({ url: '/users/me', method: 'PATCH', body }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(userUpdated((await queryFulfilled).data));
        } catch {
          // Preferences stay local; they sync on the next change.
        }
      },
    }),

  }),
});

export const {
  useOpenDevSessionMutation,
  useCheckHandleQuery,
  useRegisterMutation,
  useVerificationQuery,
  useVerifyEmailMutation,
  useResendVerificationMutation,
  useLoginMutation,
  useLogoutMutation,
  useUpdateMeMutation,
  useConfirmIdentityMutation,
  useChangePasswordMutation,
  useRequestPasswordResetMutation,
  usePasswordResetLinkQuery,
  useResetPasswordMutation,
  useBeginSecondFactorMutation,
  useEnableSecondFactorMutation,
  useLogoutEverywhereMutation,
} = authApi;

let guestStarting: Promise<unknown> | null = null;

/**
 * Make sure there is a session before doing something in a lesson: an answer, a test.
 * Without an account that is a guest's, asked for once however many parts of a page
 * want it at the same moment: two requests would make two guests.
 */
export const ensureSession = createAsyncThunk<void, string, { state: WithSession }>(
  'session/ensure',
  async (locale, { dispatch, getState }) => {
    if (getState().session.status === 'authenticated') return;
    guestStarting ??= dispatch(authApi.endpoints.startGuest.initiate({ locale }))
      .unwrap()
      .finally(() => {
        guestStarting = null;
      });
    await guestStarting;
  },
);

/** On startup: turn the refresh cookie, if there is one, into a session. */
export const restoreSession = createAsyncThunk<RefreshOutcome>('session/restore', (_, thunkApi) =>
  refreshSession({ ...thunkApi, endpoint: 'restoreSession', type: 'mutation' }),
);
